import React, { useEffect, useState } from 'react'
import { ContentCard, Flex, RoundTabs, StyledTabProps } from '@mprolab/uikit'
import MainWrapper from 'layouts/MainWrapper'
import styled from 'styled-components'
import { useRouter } from 'hooks/useRouter'
import Image from 'components/CompatImage'
import useScreenSize from 'hooks/useScreenSize'
import { PromoBanners } from 'components/PromoBanners'
import { PromoBannersType } from 'components/PromoBanners/PromoBanners'
import dottedBackground from '../../public/images/dottedBackgroundPattern1.webp'
import { Bridge, Swap } from './components'

/**
 * The trading module, served at **`/swap`** since 2026-09-02; `/exchange`
 * redirects to it, query and all.
 *
 * The folder keeps the name `Exchange` on purpose. What lives here is two
 * tools, and one of them is the bridge — `views/Swap/components/Swap/Swap.tsx`
 * would be a worse lie than a folder whose name is broader than its route. The
 * route is named for what people arrive for; the folder for what it holds.
 */

const DottedBackgroundWrapper = styled(Flex)`
  position: absolute;
  top: -150px;
  left: 70px;
  width: 100%;
  height: 700px;
  z-index: -1;
  ${({ theme }) => theme.mediaQueries.tablet} {
    height: 550px;
    left: 0;
    left: 80px;
  }
`

const SwitchWrapper = styled(Flex)`
  gap: 8px;
  padding: 8px;
  cursor: pointer;
  width: max-content;
  border-radius: 16px;
  transition: all 0.3s ease-in-out;
  background: ${({ theme }) => theme.colors.gray10};
`

/*
 * Two tabs again. Swap returned in scope 13 as an aggregator over Aerodrome,
 * Uniswap and KyberSwap on Base — a different engine from the one that was
 * removed, in the same shell, because bridging and swapping are one errand to
 * the person doing them. The onramp tab has not come back and still redirects.
 */
const tabs: StyledTabProps[] = [
  {
    label: 'Bridge',
    value: 0,
    queryParamValue: 'bridge'
  },
  {
    label: 'Swap',
    value: 1,
    queryParamValue: 'swap'
  }
]

/**
 * Which tab a bare `/swap` opens.
 *
 * It used to be the bridge, because the module lived at `/exchange` and
 * neither tab had a claim on the bare path. The module is at `/swap` now, so
 * the bare path has an obvious meaning and defaulting to the other tool would
 * be a small lie told by the URL. Bridge is one press, and the header links to
 * it with `?tab=bridge`.
 */
const DEFAULT_TAB = 'swap'
const DEFAULT_TAB_INDEX = tabs.findIndex((tab) => tab.queryParamValue === DEFAULT_TAB)

const Exchange: React.FC = () => {
  const { isMobile } = useScreenSize()
  const { replace, query } = useRouter()
  const [activeTab, setActiveTab] = useState(0)

  const getActiveTabContent = (activeTab: number) => {
    switch (activeTab) {
      case 0:
        return <Bridge />
      case 1:
        return <Swap />
      default:
        return <></>
    }
  }

  const handleTabChange = (_, tab) => {
    const selectedTab = tabs[tab]
    setActiveTab(tab)
    if (selectedTab.queryParamValue) {
      replace({ query: { tab: selectedTab.queryParamValue } }, undefined, { shallow: true })
    }
  }

  useEffect(() => {
    /*
     * The retired onramp tab. It lands on Swap rather than Bridge: somebody
     * following a "buy crypto" link wants to acquire a token, which is what
     * the swap does — the bridge only moves one they already hold.
     */
    if (query.tab === 'buy-crypto') {
      replace({ query: { tab: DEFAULT_TAB } }, undefined, { shallow: true })
      setActiveTab(DEFAULT_TAB_INDEX)
      return
    }
    const idx = tabs.findIndex((tab) => tab.queryParamValue === query.tab)
    if (idx >= 0) {
      setActiveTab(idx)
    } else {
      replace({ query: { tab: DEFAULT_TAB } }, undefined, { shallow: true })
      setActiveTab(DEFAULT_TAB_INDEX)
    }
  }, [query.tab, replace])

  const isBridgeTab = query.tab === 'bridge'

  return (
    <>
      {isBridgeTab && (
        <Flex pt="144px">
          <PromoBanners type={PromoBannersType.BRIDGE} />
        </Flex>
      )}
      <MainWrapper>
        <Flex
          fullWidth
          mt={isBridgeTab ? '40px' : isMobile ? '124px' : '192px'}
          mb={isMobile ? '124px' : '347px'}
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <ContentCard fullWidth maxWidth="590px" p={isMobile ? '24px 10px' : '24px'} flexDirection="column" gap="32px">
            <SwitchWrapper>
              <RoundTabs withBackground value={activeTab} tabs={tabs} onChange={handleTabChange} />
            </SwitchWrapper>
            {getActiveTabContent(activeTab)}
          </ContentCard>
        </Flex>
        <DottedBackgroundWrapper>
          <Image src={dottedBackground} objectFit="cover" loading="eager" />
        </DottedBackgroundWrapper>
      </MainWrapper>
    </>
  )
}

export default Exchange
